import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
} from 'lucide-react';
import './Dashboard.css';
import { API_BASE_URL } from '../global';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function Dashboard() {
  const [summary, setSummary] = useState({
    receitas_mes: 0,
    despesas_mes: 0,
    saldo_atual: 0,
    a_receber: 0,
    a_receber_pedidos: 0,
    perc_receitas: 0,
    perc_despesas: 0,
    perc_saldo: 0,
    perc_a_receber: 0,
    ultimos_pedidos: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard.php`);
      const data = await response.json();
      setSummary(data);
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const formatPerc = (val) => {
    const num = Number(val || 0);
    return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  const summaryCards = [
    { title: 'Receitas (Mês)', value: formatCurrency(summary.receitas_mes), icon: TrendingUp, type: summary.perc_receitas >= 0 ? 'success' : 'danger', change: formatPerc(summary.perc_receitas) },
    { title: 'Despesas (Mês)', value: formatCurrency(summary.despesas_mes), icon: TrendingDown, type: summary.perc_despesas <= 0 ? 'success' : 'danger', change: formatPerc(summary.perc_despesas) },
    { title: 'Saldo (Mês)', value: formatCurrency(summary.saldo_atual), icon: DollarSign, type: summary.perc_saldo >= 0 ? 'primary' : 'danger', change: formatPerc(summary.perc_saldo) },
    { title: 'A Receber', value: formatCurrency(summary.a_receber), icon: CreditCard, type: summary.perc_a_receber <= 0 ? 'success' : 'warning', change: formatPerc(summary.perc_a_receber) },
  ];

  return (
    <div className="dashboard-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Visão geral do sistema de gerenciamento financeiro Alefolia</p>
        </div>
      </div>

      <div className="summary-grid">
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="summary-card card">
              <div className="summary-card-header">
                <div>
                  <h3 className="summary-title">{card.title}</h3>
                  <div className="summary-value">{card.value}</div>
                </div>
                <div className={`summary-icon-wrapper bg-${card.type}`}>
                  <Icon size={24} className={`icon-${card.type}`} />
                </div>
              </div>
              <div className="summary-card-footer">
                <span className={`change-badge text-${card.type}`}>
                  {card.change}
                </span>
                <span className="change-text">vs. mês anterior</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-charts">
        <div className="card chart-card" style={{ flex: 2 }}>
          <div className="card-header">
            <h3>Fluxo de Caixa (Últimos 12 meses)</h3>
          </div>
          <div style={{ height: '300px', marginTop: '1rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary.fluxo_caixa || []} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} />
                <YAxis stroke="var(--text-muted)" fontSize={12} />
                <RechartsTooltip contentStyle={{ backgroundColor: 'var(--panel-bg)', borderColor: 'var(--border-color)', color: 'var(--text-main)' }} />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Line type="monotone" dataKey="receitas" name="Receitas" stroke="var(--success-color)" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="despesas" name="Despesas" stroke="var(--danger-color)" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card chart-card" style={{ flex: 1 }}>
          <div className="card-header">
            <h3>Produtos Mais Vendidos</h3>
          </div>
          <div style={{ height: '300px', marginTop: '1rem' }}>
            {(!summary.produtos_vendidos || summary.produtos_vendidos.length === 0) ? (
              <div className="chart-placeholder flex-center" style={{ height: '100%' }}>
                <p className="text-muted">Nenhum dado disponível</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={summary.produtos_vendidos}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {summary.produtos_vendidos.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip contentStyle={{ backgroundColor: 'var(--panel-bg)', borderColor: 'var(--border-color)', color: 'var(--text-main)' }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-lists">
        <div className="card list-card">
          <div className="card-header">
            <h3>Últimos Pedidos</h3>
          </div>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {summary.ultimos_pedidos.map((pedido) => (
                  <tr key={pedido.id}>
                    <td>#{pedido.venda_id}</td>
                    <td>{pedido.cliente}</td>
                    <td>{formatCurrency(pedido.valor)}</td>
                    <td><span className="badge badge-success">Concluído</span></td>
                  </tr>
                ))}
                {summary.ultimos_pedidos.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '1rem' }}>
                      Nenhum pedido encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
